import { useCallback, useRef, useState } from 'react'
import { ApolloError } from '@apollo/client'
import { v4 as uuidv4 } from 'uuid'
import { describeFailure, Failure, failureText } from '../../api/failureReason'
import { advancedGet, advancedPost } from '../../api/client'
import { useAdvancedResource } from '../../api/useAdvancedResource'
import { useRefreshWhileVisible } from '../../api/useRefreshWhileVisible'
import { CareTeamChatMessage, ForwardedFeedItem, MessageAttachment, SendMessageError } from './types'
import { PendingAttachment } from './attachments/AttachmentPicker'
import { useRetainAdvancedChatFilesMutation } from './attachments/retainAdvancedChatFiles.generated'

const POLL_MS = 15_000

const messagesPath = (uid: string, threadId: string | null) =>
  threadId
    ? `/patients/${encodeURIComponent(uid)}/care-team-chat/threads/${encodeURIComponent(threadId)}/messages`
    : `/patients/${encodeURIComponent(uid)}/care-team-chat/messages`

type MessageRow = {
  messageId: string
  providerUid: string
  body: string
  forwardedItem: ForwardedFeedItem | null
  attachments: Omit<MessageAttachment, 'downloadURL'>[] | null
  createdAt: string
}

const toChatMessage = (row: MessageRow): CareTeamChatMessage => ({
  id: row.messageId,
  authorCoachId: row.providerUid,
  text: row.body,
  forwardedItem: row.forwardedItem,
  attachments: (row.attachments ?? []).map((attachment) => ({ ...attachment, downloadURL: null })),
  createdAt: row.createdAt,
})

export type CareTeamChatState = {
  messages: CareTeamChatMessage[]
  // The provider's read marker: `undefined` until it loads, `null` with none.
  readThrough: string | null | undefined
  refresh: () => void
  loading: boolean
  sending: boolean
  error: Failure | null
  sendMessage: (
    text: string,
    attachments?: PendingAttachment[],
    forwardedItem?: ForwardedFeedItem | null
  ) => Promise<void>
}

export const useCareTeamChat = (uid: string, threadId: string | null = null): CareTeamChatState => {
  // Tagged so a thread switch never shows the previous thread's messages.
  const threadKey = `${uid} ${threadId ?? ''}`
  const resource = useAdvancedResource<{ threadKey: string; messages: MessageRow[]; readThrough: string | null }>(
    async (signal) => {
      const { messages, readThrough } = await advancedGet<{ messages: MessageRow[]; readThrough: string | null }>(
        messagesPath(uid, threadId),
        signal
      )
      return { threadKey, messages, readThrough }
    },
    [uid, threadId]
  )
  useRefreshWhileVisible(resource.retry, POLL_MS)
  const [sent, setSent] = useState<{ threadKey: string; messages: CareTeamChatMessage[] }>()
  const [sending, setSending] = useState(false)
  const [retainFiles] = useRetainAdvancedChatFilesMutation()

  // Still the previous thread's data for one render after threadId changes; gate on the fetch's own key.
  const loaded =
    resource.status === 'ready' && resource.data.threadKey === threadKey
      ? resource.data.messages.map(toChatMessage)
      : []
  const messages =
    sent?.threadKey === threadKey
      ? [...loaded, ...sent.messages.filter((message) => !loaded.some((known) => known.id === message.id))]
      : loaded

  const lastAttempt = useRef<{ key: string; messageId: string } | null>(null)

  const sendMessage = useCallback(
    async (text: string, attachments: PendingAttachment[] = [], forwardedItem: ForwardedFeedItem | null = null) => {
      setSending(true)
      // Client-minted (ADR-088); a resent failed draft reuses it, so the send replays.
      const attempt = JSON.stringify([threadKey, text, attachments.map((file) => file.fileID), forwardedItem])
      const messageId = lastAttempt.current?.key === attempt ? lastAttempt.current.messageId : uuidv4()
      lastAttempt.current = { key: attempt, messageId }
      try {
        // Retained first so an unsafe file never reaches a real message.
        const retained =
          attachments.length > 0
            ? await retainFiles({
                variables: { uid, messageID: messageId, fileIDs: attachments.map((file) => file.fileID) },
              })
            : undefined

        const { message } = await advancedPost<{ message: MessageRow }>(messagesPath(uid, threadId), {
          messageId,
          text,
          forwardedItem,
          // Rebuilt so codegen's `__typename` stays out of jsonb.
          attachments: (retained?.data?.retainAdvancedChatFiles ?? []).map(({ fileID, filename, contentType }) => ({
            fileID,
            filename,
            contentType,
          })),
        })
        lastAttempt.current = null
        setSent((current) => {
          const existing = current?.threadKey === threadKey ? current.messages : []
          // A double submit replays the same id, and both responses land here.
          if (existing.some((known) => known.id === message.messageId)) {
            return current
          }
          return { threadKey, messages: [...existing, toChatMessage(message)] }
        })
      } catch (cause) {
        const graphQLError = cause instanceof ApolloError ? cause.graphQLErrors[0] : undefined
        // Rethrown so the composer keeps the draft.
        const described = describeFailure('Failed to send this message — it has not reached the care team.', cause)
        const failure: SendMessageError = Object.assign(new Error(failureText(described)), { failure: described })
        const rejected = graphQLError?.extensions?.rejectedFileIDs
        if (Array.isArray(rejected)) {
          failure.rejectedFileIDs = rejected.filter((fileID): fileID is string => typeof fileID === 'string')
        }
        throw failure
      } finally {
        setSending(false)
      }
    },
    [uid, threadId, threadKey, retainFiles]
  )

  return {
    messages,
    readThrough:
      resource.status === 'ready' && resource.data.threadKey === threadKey ? resource.data.readThrough : undefined,
    refresh: resource.retry,
    loading: resource.status === 'loading',
    sending,
    error: resource.status === 'error' ? describeFailure('Failed to load this conversation.', resource.error) : null,
    sendMessage,
  }
}
